<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import { useAlert } from 'dashboard/composables';
import AiAPI from 'custom/api/ai';
import { intlLocale, useLocoI18n } from 'custom/composables/useLocoI18n';
import { useAiSettings } from './useAiSettings';
import { AI_PROVIDERS, needsApiBase, providerLabel } from './providers';
import { formatCost, formatTokens } from './helpers';

// AI settings: the account's provider, key and models, a monthly budget, and what AI cost this month
const { t, locale } = useLocoI18n();
const route = useRoute();
const { load, set } = useAiSettings();

const current = ref(null);
const usage = ref(null);
const isSaving = ref(false);
const isTesting = ref(false);
const testResult = ref(null);
const form = reactive({
  provider: 'openai',
  api_key: '',
  api_base: '',
  chat_model: '',
  embedding_provider: '',
  embedding_api_key: '',
  embedding_model: '',
  monthly_budget: '',
});

const USAGE_KINDS = [
  'reply',
  'compose',
  'copilot',
  'embedding',
  'learning',
  'playground',
];

const defaults = computed(() => current.value?.providers || {});
const providers = computed(() => Object.keys(defaults.value));
const embeddingProviders = computed(() =>
  providers.value.filter(provider => defaults.value[provider]?.embeddings)
);
const provider = computed(() => AI_PROVIDERS[form.provider] || {});
const embeddingProvider = computed(
  () =>
    form.embedding_provider ||
    (defaults.value[form.provider]?.embeddings ? form.provider : '')
);
const keySaved = computed(
  () => current.value?.api_key_set && current.value?.provider === form.provider
);
const embeddingKeySaved = computed(
  () =>
    current.value?.embedding_api_key_set &&
    current.value?.embedding_provider === form.embedding_provider
);
const needsEmbeddingKey = computed(
  () =>
    form.embedding_provider &&
    form.embedding_provider !== form.provider &&
    !AI_PROVIDERS[form.embedding_provider]?.noKey
);
const usageRows = computed(() =>
  USAGE_KINDS.map(kind => ({
    kind,
    ...(usage.value?.by_kind?.find(row => row.kind === kind) || {}),
  })).filter(row => row.cost !== undefined)
);

const money = value => formatCost(value, intlLocale(locale.value));
const tokens = value => formatTokens(value, intlLocale(locale.value));

const fill = data => {
  current.value = data;
  Object.assign(form, {
    provider: data.provider || 'openai',
    api_key: '',
    api_base: data.api_base || '',
    chat_model: data.chat_model || '',
    embedding_provider: data.embedding_provider || '',
    embedding_api_key: '',
    embedding_model: data.embedding_model || '',
    monthly_budget: data.monthly_budget ?? '',
  });
};

const loadUsage = async () => {
  if (!current.value?.configured) return;
  const { data } = await AiAPI.usage();
  usage.value = data;
};

const errorMessage = error => error.response?.data?.error || error.message;

const save = async () => {
  isSaving.value = true;
  testResult.value = null;
  try {
    const { data } = await AiAPI.updateSettings({
      ...form,
      monthly_budget: form.monthly_budget === '' ? null : form.monthly_budget,
    });
    fill(data);
    set(data);
    useAlert(t('LOCO.AI.SETTINGS.SAVED'));
    loadUsage();
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const test = async () => {
  isTesting.value = true;
  testResult.value = null;
  try {
    const { data } = await AiAPI.testSettings();
    testResult.value = {
      ok: true,
      text: t('LOCO.AI.SETTINGS.TEST_OK', { model: data.model }),
    };
  } catch (error) {
    testResult.value = { ok: false, text: errorMessage(error) };
  } finally {
    isTesting.value = false;
  }
};

onMounted(async () => {
  fill(await load({ force: true }));
  loadUsage();
});
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="max-w-3xl mx-auto px-6 py-8 flex flex-col gap-8">
      <header class="flex flex-col gap-1">
        <h1 class="text-heading-1 text-n-slate-12">
          {{ t('LOCO.AI.SETTINGS.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('LOCO.AI.SETTINGS.SUBTITLE') }}
        </p>
      </header>

      <form v-if="current" class="flex flex-col gap-8" @submit.prevent="save">
        <section class="flex flex-col gap-4">
          <h2 class="text-heading-3 text-n-slate-12">
            {{ t('LOCO.AI.SETTINGS.PROVIDER') }}
          </h2>
          <div class="grid gap-4 md:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm">
              <span class="font-medium text-n-slate-12">
                {{ t('LOCO.AI.SETTINGS.PROVIDER') }}
              </span>
              <select v-model="form.provider" class="!mb-0">
                <option v-for="item in providers" :key="item" :value="item">
                  {{ providerLabel(item, t) }}
                </option>
              </select>
            </label>
            <label v-if="!provider.noKey" class="flex flex-col gap-1 text-sm">
              <span class="flex items-center justify-between gap-2">
                <span class="font-medium text-n-slate-12">
                  {{ t('LOCO.AI.SETTINGS.API_KEY') }}
                </span>
                <a
                  v-if="provider.keyUrl"
                  :href="provider.keyUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-xs text-n-blue-11 hover:underline"
                >
                  {{ t('LOCO.AI.SETTINGS.GET_KEY') }}
                </a>
              </span>
              <input
                v-model="form.api_key"
                type="password"
                autocomplete="off"
                class="!mb-0"
                :placeholder="
                  keySaved
                    ? t('LOCO.AI.SETTINGS.KEY_SAVED')
                    : t('LOCO.AI.SETTINGS.KEY_PLACEHOLDER')
                "
              />
            </label>
            <label
              v-if="needsApiBase(form.provider)"
              class="flex flex-col gap-1 text-sm"
            >
              <span class="font-medium text-n-slate-12">
                {{ t('LOCO.AI.SETTINGS.API_BASE') }}
              </span>
              <input
                v-model="form.api_base"
                type="url"
                class="!mb-0"
                :placeholder="provider.basePlaceholder"
              />
            </label>
            <label class="flex flex-col gap-1 text-sm">
              <span class="font-medium text-n-slate-12">
                {{ t('LOCO.AI.SETTINGS.CHAT_MODEL') }}
              </span>
              <input
                v-model="form.chat_model"
                type="text"
                class="!mb-0"
                :placeholder="defaults[form.provider]?.chat_model || ''"
              />
              <span class="text-xs text-n-slate-11">
                {{ t('LOCO.AI.SETTINGS.CHAT_MODEL_HELP') }}
              </span>
            </label>
          </div>
        </section>

        <section class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <h2 class="text-heading-3 text-n-slate-12">
              {{ t('LOCO.AI.SETTINGS.EMBEDDINGS') }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ t('LOCO.AI.SETTINGS.EMBEDDINGS_HELP') }}
            </p>
          </div>
          <Banner v-if="!embeddingProvider" color="amber">
            {{
              t('LOCO.AI.SETTINGS.NO_EMBEDDINGS', {
                provider: providerLabel(form.provider, t),
              })
            }}
          </Banner>
          <div class="grid gap-4 md:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm">
              <span class="font-medium text-n-slate-12">
                {{ t('LOCO.AI.SETTINGS.EMBEDDING_PROVIDER') }}
              </span>
              <select v-model="form.embedding_provider" class="!mb-0">
                <option value="">
                  {{ t('LOCO.AI.SETTINGS.SAME_PROVIDER') }}
                </option>
                <option
                  v-for="item in embeddingProviders"
                  :key="item"
                  :value="item"
                >
                  {{ providerLabel(item, t) }}
                </option>
              </select>
            </label>
            <label v-if="needsEmbeddingKey" class="flex flex-col gap-1 text-sm">
              <span class="font-medium text-n-slate-12">
                {{ t('LOCO.AI.SETTINGS.EMBEDDING_KEY') }}
              </span>
              <input
                v-model="form.embedding_api_key"
                type="password"
                autocomplete="off"
                class="!mb-0"
                :placeholder="
                  embeddingKeySaved
                    ? t('LOCO.AI.SETTINGS.KEY_SAVED')
                    : t('LOCO.AI.SETTINGS.KEY_PLACEHOLDER')
                "
              />
            </label>
            <label v-if="embeddingProvider" class="flex flex-col gap-1 text-sm">
              <span class="font-medium text-n-slate-12">
                {{ t('LOCO.AI.SETTINGS.EMBEDDING_MODEL') }}
              </span>
              <input
                v-model="form.embedding_model"
                type="text"
                class="!mb-0"
                :placeholder="
                  defaults[embeddingProvider]?.embedding_model || ''
                "
              />
            </label>
          </div>
        </section>

        <section class="flex flex-col gap-4">
          <h2 class="text-heading-3 text-n-slate-12">
            {{ t('LOCO.AI.SETTINGS.BUDGET') }}
          </h2>
          <label class="flex flex-col gap-1 text-sm max-w-xs">
            <span class="font-medium text-n-slate-12">
              {{ t('LOCO.AI.SETTINGS.MONTHLY_BUDGET') }}
            </span>
            <input
              v-model="form.monthly_budget"
              type="number"
              min="0"
              step="0.01"
              class="!mb-0"
              :placeholder="t('LOCO.AI.SETTINGS.NO_LIMIT')"
            />
          </label>
          <p class="text-xs text-n-slate-11 -mt-2">
            {{ t('LOCO.AI.SETTINGS.BUDGET_HELP') }}
          </p>
        </section>

        <div class="flex flex-wrap items-center gap-3">
          <Button
            type="submit"
            :label="t('LOCO.AI.SETTINGS.SAVE')"
            :is-loading="isSaving"
          />
          <Button
            type="button"
            faded
            slate
            icon="i-lucide-plug-zap"
            :label="t('LOCO.AI.SETTINGS.TEST')"
            :is-loading="isTesting"
            :disabled="!current.configured"
            @click="test"
          />
          <span
            v-if="testResult"
            class="text-sm"
            :class="testResult.ok ? 'text-n-teal-11' : 'text-n-ruby-11'"
          >
            {{ testResult.text }}
          </span>
        </div>
      </form>

      <section v-if="usage" class="flex flex-col gap-3">
        <div class="flex flex-wrap items-baseline justify-between gap-2">
          <h2 class="text-heading-3 text-n-slate-12">
            {{ t('LOCO.AI.SETTINGS.USAGE') }}
          </h2>
          <span class="text-sm text-n-slate-11">
            {{
              current?.monthly_budget
                ? t('LOCO.AI.SETTINGS.SPENT_OF', {
                    spent: money(usage.cost),
                    budget: money(current.monthly_budget),
                  })
                : t('LOCO.AI.SETTINGS.SPENT', { spent: money(usage.cost) })
            }}
          </span>
        </div>
        <p v-if="!usageRows.length" class="text-sm text-n-slate-11">
          {{ t('LOCO.AI.SETTINGS.NO_USAGE') }}
        </p>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-start text-xs text-n-slate-11">
              <th class="py-2 font-medium text-start">
                {{ t('LOCO.AI.SETTINGS.USAGE_KIND') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('LOCO.AI.SETTINGS.TOKENS_IN') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('LOCO.AI.SETTINGS.TOKENS_OUT') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('LOCO.AI.SETTINGS.COST') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in usageRows"
              :key="row.kind"
              class="border-t border-n-weak text-n-slate-12"
            >
              <td class="py-2">
                {{ t(`LOCO.AI.SETTINGS.KINDS.${row.kind.toUpperCase()}`) }}
              </td>
              <td class="py-2 text-end">{{ tokens(row.input_tokens) }}</td>
              <td class="py-2 text-end">{{ tokens(row.output_tokens) }}</td>
              <td class="py-2 text-end">{{ money(row.cost) }}</td>
            </tr>
          </tbody>
        </table>

        <template v-if="usage.conversations.length">
          <h3 class="text-sm font-medium text-n-slate-12 mt-4">
            {{ t('LOCO.AI.SETTINGS.CONVERSATIONS') }}
          </h3>
          <table class="w-full text-sm">
            <thead>
              <tr class="text-xs text-n-slate-11">
                <th class="py-2 font-medium text-start">
                  {{ t('LOCO.AI.SETTINGS.CONVERSATION') }}
                </th>
                <th class="py-2 font-medium text-start">
                  {{ t('LOCO.AI.SETTINGS.INBOX') }}
                </th>
                <th class="py-2 font-medium text-end">
                  {{ t('LOCO.AI.SETTINGS.TOKENS') }}
                </th>
                <th class="py-2 font-medium text-end">
                  {{ t('LOCO.AI.SETTINGS.COST') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in usage.conversations"
                :key="row.display_id"
                class="border-t border-n-weak text-n-slate-12"
              >
                <td class="py-2">
                  <router-link
                    :to="{
                      name: 'inbox_conversation',
                      params: {
                        accountId: route.params.accountId,
                        conversation_id: row.display_id,
                      },
                    }"
                    class="text-n-blue-11 hover:underline"
                  >
                    {{
                      t('LOCO.AI.SETTINGS.CONVERSATION_LINK', {
                        id: row.display_id,
                        name: row.contact_name,
                      })
                    }}
                  </router-link>
                </td>
                <td class="py-2 text-n-slate-11">{{ row.inbox_name }}</td>
                <td class="py-2 text-end">{{ tokens(row.tokens) }}</td>
                <td class="py-2 text-end">{{ money(row.cost) }}</td>
              </tr>
            </tbody>
          </table>
        </template>
        <p class="text-xs text-n-slate-10">
          {{ t('LOCO.AI.SETTINGS.COST_NOTE') }}
        </p>
      </section>
    </div>
  </div>
</template>
